import type { User } from 'firebase/auth';
import { defineStore, getActivePinia, type Pinia } from 'pinia';
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useFirebaseAuth } from 'vuefire';
import type { AuthIdentifier } from '@/service/types';

function disposeUserStores(pinia: Pinia): void {
  const storeMap = (
    pinia as unknown as { _s: Map<string, { $dispose(): void }> }
  )._s;
  const userStores = ['recipe', 'groceries', 'profile'];
  for (const id of userStores) {
    const store = storeMap.get(id);
    if (store) {
      store.$dispose();
      storeMap.delete(id);
    }
  }
}

export const useAuthStore = defineStore(
  'auth',
  () => {
    const auth = useFirebaseAuth();
    const router = useRouter();

    const authenticated = ref(false);
    const user = ref<User>();
    return {
      authenticated,
      user,
      setUser(value: User): void {
        authenticated.value = true;
        user.value = value;
      },
      getUser(): User {
        if (user.value) {
          return user.value;
        }
        throw new Error('No user');
      },
      getIdentifier(): AuthIdentifier {
        if (user.value) {
          return { uid: user.value?.uid };
        }
        throw new Error('No user');
      },
      async logout(): Promise<void> {
        await auth?.signOut();
        authenticated.value = false;
        user.value = undefined;
        // Dispose user-specific stores so they get recreated for the next user
        const pinia = getActivePinia();
        if (pinia) {
          disposeUserStores(pinia);
        }
        localStorage.removeItem('groceries');
        await router.push({ name: 'login' });
      },
    };
  },
  {
    persist: {
      storage: localStorage,
      pick: ['theme', 'authenticated', 'user'],
    },
  },
);
